import { Component, OnInit, ChangeDetectionStrategy, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';

import { ProgressBarModule } from '@openng/optimus-ui/progressbar';
import { TagModule } from '@openng/optimus-ui/tag';
import { ButtonModule } from '@openng/optimus-ui/button';

import { ActiveImport, ImportProgressService, ImportStatus } from 'src/app/services/import-progress.service';

@Component({
	selector: 'app-import-progress-banner',
	standalone: true,
	imports: [ProgressBarModule, TagModule, ButtonModule, NgClass],
	templateUrl: './import-progress-banner.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['./import-progress-banner.component.scss'],
})
export class ImportProgressBannerComponent implements OnInit {
	importProgressService = inject(ImportProgressService);

	private destroyRef = inject(DestroyRef);

	imports = signal<ActiveImport[]>([]);

	expanded = signal(false);

	ngOnInit(): void {
		this.importProgressService.activeImports$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(imports => {
			this.imports.set(imports);
			if (imports.length === 0) {
				this.expanded.set(false);
			}
		});
	}

	public toggleExpanded(): void {
		this.expanded.update(expanded => !expanded);
	}

	public progressPercent(imp: ActiveImport): number {
		if (imp.status === 'COMPLETED') return 100;
		if (!imp.totalDocuments || imp.totalDocuments === 0) return 0;
		return Math.round((imp.documentsIndexed / imp.totalDocuments) * 100);
	}

	/**
	 * Whether to show a progress bar for this import. Shown while in flight (PREPARING has an
	 * indeterminate bar; STARTED/INDEXING a determinate one) and on COMPLETED, where it sits
	 * filled at 100% so the finished import reads clearly as done.
	 * @param imp
	 */
	public showProgressBar(imp: ActiveImport): boolean {
		return imp.status === 'PREPARING' || imp.status === 'COMPLETED' || (imp.totalDocuments > 0 && (imp.status === 'INDEXING' || imp.status === 'STARTED'));
	}

	/** Summary-row icon: a green check once everything is done, otherwise the (spinning) sync icon. */
	public summaryIcon(): string {
		return this.allDone ? 'pi pi-check-circle' : 'pi pi-sync';
	}

	/** True when every tracked import has finished (COMPLETED or FAILED) and none is still in flight. */
	public get allDone(): boolean {
		return this.imports().length > 0 && !this.isActive;
	}

	/** Collapsed multi-import done tag: 'COMPLETED' when all succeeded, else 'FAILED'. */
	public get summaryTagValue(): string {
		return this.imports().some(i => i.status === 'FAILED') ? 'FAILED' : 'COMPLETED';
	}

	public get summaryTagSeverity(): 'success' | 'danger' {
		return this.imports().some(i => i.status === 'FAILED') ? 'danger' : 'success';
	}

	public tagValue(status: ImportStatus): string {
		return status === 'PREPARING' ? 'Preparing' : status;
	}

	public tagSeverity(status: ImportStatus): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
		switch (status) {
			case 'COMPLETED':
				return 'success';
			case 'FAILED':
				return 'danger';
			case 'INDEXING':
				return 'info';
			case 'PREPARING':
				return 'secondary';
			default:
				return 'secondary';
		}
	}

	public statusLabel(imp: ActiveImport): string {
		switch (imp.status) {
			case 'PREPARING':
				return 'Preparing...';
			case 'STARTED':
				return imp.totalDocuments > 0 ? `Starting... (${imp.totalDocuments} documents)` : 'Starting...';
			case 'INDEXING':
				return imp.totalDocuments > 0 ? `Indexing ${imp.documentsIndexed} / ${imp.totalDocuments}` : 'Indexing...';
			case 'COMPLETED':
				return `Done — ${imp.totalDocuments} documents`;
			case 'FAILED':
				return imp.message ? `Failed: ${imp.message}` : 'Failed';
			default:
				return imp.status;
		}
	}

	public get isActive(): boolean {
		return this.imports().some(i => i.status === 'PREPARING' || i.status === 'STARTED' || i.status === 'INDEXING');
	}

	public get summaryLabel(): string {
		const imports = this.imports();
		if (imports.length === 1) {
			const imp = imports[0];
			const name = imp.mainSuiteName || imp.mainSuiteId;
			// Once done, make the collapsed row explicitly say so — the tag alone is easy to miss.
			if (imp.status === 'COMPLETED') {
				return `${name} — done`;
			}
			return name;
		}
		// Once every suite has finished, say so in the collapsed row so the user doesn't
		// have to expand to see it's done. Call out any failures.
		if (this.allDone) {
			const failed = imports.filter(i => i.status === 'FAILED').length;
			if (failed > 0) {
				return `${imports.length} suites imported — ${failed} failed`;
			}
			return `${imports.length} suites imported — done`;
		}
		const indexing = imports.filter(i => i.status === 'PREPARING' || i.status === 'INDEXING' || i.status === 'STARTED').length;
		return `Importing ${imports.length} suites${indexing > 0 ? ` (${indexing} active)` : ''}`;
	}
}
